import { NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, output, signal } from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Title } from '@angular/platform-browser';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { CheckboxModule } from 'primeng/checkbox';
import { DialogModule } from 'primeng/dialog';
import { FloatLabelModule } from 'primeng/floatlabel';
import { IconFieldModule } from 'primeng/iconfield';
import { ImageModule } from 'primeng/image';
import { InputIconModule } from 'primeng/inputicon';
import { InputTextModule } from 'primeng/inputtext';
import { MessageModule } from 'primeng/message';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { catchError, of, tap } from 'rxjs';
import { AuthService } from '../../_core/auth.service';
import { BrandingService } from '../../_core/branding.service';
import { parseLocalizedAmount } from '../../utils/parse-localized-amount';
import {
  normalizeSpanishMobilePhone,
  optionalSpanishMobileValidator,
} from '../../utils/optional-spanish-mobile.validator';

@Component({
  selector: 'app-register',
  imports: [
    ButtonModule,
    CardModule,
    CheckboxModule,
    DialogModule,
    FloatLabelModule,
    IconFieldModule,
    ImageModule,
    InputIconModule,
    InputTextModule,
    MessageModule,
    NgTemplateOutlet,
    ProgressSpinnerModule,
    ReactiveFormsModule,
  ],
  templateUrl: './register.html',
  styleUrl: './register.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class Register {
  protected readonly registerStep = signal<'form' | 'loading' | 'success'>('form');
  protected readonly registeredEmail = signal('');
  readonly wizardStep = signal<1 | 2 | 3>(1);
  protected readonly showPhonePrivacyDialog = signal(false);
  protected readonly showLiquidationHelp = signal(false);
  private static readonly REGISTER_VERIFY_ERROR = 'No se ha podido verificar los datos, revíselos.';
  private acceptedPhone: string | undefined;

  protected navigate = output<string>();

  readonly title = inject(Title).setTitle('Oficina Virtual - Registro');
  private $auth = inject(AuthService);
  protected $branding = inject(BrandingService);
  private formBuilder = inject(FormBuilder);
  public form: FormGroup = this.initForm();
  public checkConditions: FormControl = new FormControl(false, Validators.required);
  public checkPhoneConditions: FormControl = new FormControl(false);
  public msgError: string = '';

  readonly steps = [
    { id: 1, label: 'Email' },
    { id: 2, label: 'Teléfono' },
    { id: 3, label: 'Liquidación' },
  ] as const;

  constructor() {
    this.form
      .get('phone')!
      .valueChanges.pipe(takeUntilDestroyed())
      .subscribe((value) => {
        const phone = normalizeSpanishMobilePhone(value);
        if (this.checkPhoneConditions.value === true && phone !== this.acceptedPhone) {
          this.checkPhoneConditions.setValue(false, { emitEvent: false });
        }
      });
  }

  private initForm(): FormGroup {
    return this.formBuilder.group({
      email: [null, Validators.compose([Validators.email, Validators.required])],
      phone: [null, optionalSpanishMobileValidator()],
      liquidationCode: [null, Validators.required],
      liquidationAmount: [null, Validators.required],
    });
  }

  public submitCurrentStep(): void {
    if (this.wizardStep() === 1) {
      this.continueEmail();
      return;
    }
    if (this.wizardStep() === 2) {
      this.continuePhone();
      return;
    }
    this.submit();
  }

  public continueEmail(): void {
    const email = this.form.get('email');
    email?.markAsTouched();
    if (email?.invalid || this.checkConditions.value !== true) {
      this.checkConditions.markAsTouched();
      return;
    }
    this.wizardStep.set(2);
  }

  public continuePhone(): void {
    const phoneControl = this.form.get('phone');
    phoneControl?.markAsTouched();
    if (!this.canContinueWithPhone()) {
      if (this.$branding.isGGM()) {
        this.checkPhoneConditions.markAsTouched();
      }
      return;
    }
    this.wizardStep.set(3);
  }

  public skipPhone(): void {
    this.acceptedPhone = undefined;
    this.checkPhoneConditions.setValue(false);
    this.form.get('phone')?.setValue(null);
    this.wizardStep.set(3);
  }

  public back(): void {
    const current = this.wizardStep();
    if (current === 1) {
      return;
    }
    this.wizardStep.set((current - 1) as 1 | 2);
  }

  public canContinueWithPhone(): boolean {
    const phoneControl = this.form.get('phone');
    const phone = normalizeSpanishMobilePhone(phoneControl?.value);
    if (!phone || phoneControl?.invalid) {
      return false;
    }
    if (!this.$branding.isGGM()) {
      return true;
    }
    return this.checkPhoneConditions.value === true && this.acceptedPhone === phone;
  }

  public phoneHasValue(): boolean {
    const raw = this.form.get('phone')?.value;
    return raw != null && String(raw).trim() !== '';
  }

  public onPhoneConditionsChange(checked: boolean): void {
    if (!checked) {
      this.acceptedPhone = undefined;
      this.checkPhoneConditions.setValue(false, { emitEvent: false });
      return;
    }
    this.checkPhoneConditions.setValue(false, { emitEvent: false });
    this.showPhonePrivacyDialog.set(true);
  }

  public acceptPhonePrivacy(): void {
    this.acceptedPhone = normalizeSpanishMobilePhone(this.form.get('phone')?.value);
    this.checkPhoneConditions.setValue(true);
    this.showPhonePrivacyDialog.set(false);
  }

  public cancelPhonePrivacy(): void {
    this.acceptedPhone = undefined;
    this.checkPhoneConditions.setValue(false);
    this.checkPhoneConditions.markAsTouched();
    this.showPhonePrivacyDialog.set(false);
  }

  public submit(): void {
    const email = this.form.get('email');
    if (email?.invalid || this.checkConditions.value !== true) {
      email?.markAsTouched();
      this.checkConditions.markAsTouched();
      this.wizardStep.set(1);
      return;
    }

    const liquidationCode = this.form.get('liquidationCode');
    const liquidationAmountControl = this.form.get('liquidationAmount');
    if (liquidationCode?.invalid || liquidationAmountControl?.invalid) {
      liquidationCode?.markAsTouched();
      liquidationAmountControl?.markAsTouched();
      this.wizardStep.set(3);
      return;
    }

    const liquidationAmount = parseLocalizedAmount(this.form.value.liquidationAmount);
    if (liquidationAmount === null) {
      this.msgError = Register.REGISTER_VERIFY_ERROR;
      this.wizardStep.set(3);
      return;
    }
    this.msgError = '';

    const phone = this.canContinueWithPhone()
      ? normalizeSpanishMobilePhone(this.form.value.phone)
      : undefined;
    this.executeRegister(liquidationAmount, phone);
  }

  public toLogin(): void {
    this.navigate.emit('LOGIN');
  }

  private registerErrorMessage(err: { status?: number; error?: { code?: string } }): string {
    if (err?.error?.code === 'MULTIPLE_TENANT_USERS') {
      return 'Hay varios usuarios registrados con ese email. Contacte con atención al cliente.';
    }
    if (err?.status === 404 && err?.error?.code === 'NOT_FOUND') {
      return 'No hemos podido localizar un abonado con los datos introducidos';
    }
    return Register.REGISTER_VERIFY_ERROR;
  }

  private executeRegister(liquidationAmount: number, phone?: string): void {
    this.registerStep.set('loading');
    const email = String(this.form.value.email ?? '').trim();
    this.$auth
      .register({
        email,
        liquidationCode: this.form.value.liquidationCode,
        liquidationAmount,
        ...(phone ? { phone } : {}),
      })
      .pipe(
        tap(() => {
          this.registeredEmail.set(email);
          this.registerStep.set('success');
        }),
        catchError((err: { status?: number; error?: { code?: string } }) => {
          this.msgError = this.registerErrorMessage(err);
          this.wizardStep.set(3);
          this.registerStep.set('form');
          return of(null);
        }),
      )
      .subscribe();
  }
}
